import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format, isValid } from "date-fns";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Building2,
  Calendar,
  IndianRupee,
  Users,
  Briefcase,
  Clock,
  Award,
  Info,
  Shield,
  Activity,
} from "lucide-react";
import { useState, useEffect } from "react";
import { getEmployeesForDropdown } from "@/services/employee.service";

interface ViewEmployeeDialogProps {
  employee: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ViewEmployeeDialog({
  employee,
  open,
  onOpenChange,
}: ViewEmployeeDialogProps) {
  const [managerNames, setManagerNames] = useState<{
    reportingManager: string;
    lineManager: string;
    lineManager2: string;
  }>({
    reportingManager: 'Loading...',
    lineManager: 'Loading...',
    lineManager2: 'Loading...'
  });

  // Resolve manager names when employee data changes
  useEffect(() => {
    if (employee?.jobInfo) {
      const resolveManagerNames = async () => {
        try {
          const employees = await getEmployeesForDropdown();

          const resolveManagerNames = (managers: any): string => {
            if (!managers) {
              return 'Not provided';
            }

            // Handle both array and single value for backward compatibility
            const managerArray = Array.isArray(managers) ? managers : [managers];

            if (managerArray.length === 0) {
              return 'Not provided';
            }

            const resolvedNames = managerArray.map((manager: any) => {
              // If it's already a populated object with firstName (from backend population)
              if (typeof manager === 'object' && manager.firstName) {
                const fullName = `${manager.firstName} ${manager.lastName || ''}`.trim();
                const employeeId = manager.employeeId || 'N/A';
                return `${fullName} (${employeeId})`;
              }

              // If it's an ID string, try to find in the employees list
              if (typeof manager === 'string' && manager.trim() !== '') {
                const foundManager = employees.find((emp: any) =>
                  emp.id === manager ||
                  emp._id === manager ||
                  emp.employeeId === manager
                );

                if (foundManager && foundManager.name) {
                  const firstName = foundManager.name.split(' ')[0];
                  const employeeId = foundManager.employeeId || manager;
                  return `${firstName} (${employeeId})`;
                }

                return manager; // Return original if not found
              }

              return 'Unknown';
            }).filter(name => name !== 'Unknown');

            return resolvedNames.length > 0 ? resolvedNames.join(', ') : 'Not provided';
          };

          setManagerNames({
            reportingManager: resolveManagerNames(employee.reportingManagers || employee.jobInfo?.reportingManager || []),
            lineManager: resolveManagerNames(employee.lineManagers || employee.jobInfo?.lineManager || []),
            lineManager2: resolveManagerNames(employee.lineManagers2 || employee.jobInfo?.lineManager2 || [])
          });
        } catch (error) {
          console.error('Error resolving manager names:', error);
          setManagerNames({
            reportingManager: Array.isArray(employee.reportingManagers || employee.jobInfo?.reportingManager)
              ? (employee.reportingManagers || employee.jobInfo?.reportingManager).join(', ') || 'Not provided'
              : (employee.reportingManagers || employee.jobInfo?.reportingManager) || 'Not provided',
            lineManager: Array.isArray(employee.lineManagers || employee.jobInfo?.lineManager)
              ? (employee.lineManagers || employee.jobInfo?.lineManager).join(', ') || 'Not provided'
              : (employee.lineManagers || employee.jobInfo?.lineManager) || 'Not provided',
            lineManager2: Array.isArray(employee.lineManagers2 || employee.jobInfo?.lineManager2)
              ? (employee.lineManagers2 || employee.jobInfo?.lineManager2).join(', ') || 'Not provided'
              : (employee.lineManagers2 || employee.jobInfo?.lineManager2) || 'Not provided'
          });
        }
      };

      resolveManagerNames();
    }
  }, [employee]);

  if (!employee) return null;

  const formatDate = (date: any) => {
    if (date && isValid(new Date(date))) {
      return format(new Date(date), "PPP");
    }
    return "Not specified";
  };

  const formatCurrency = (amount: any) => {
    if (amount && !isNaN(amount)) {
      return `₹${Number(amount).toLocaleString("en-IN")}`;
    }
    return "Not specified";
  };

  const renderInfoItem = (
    icon: React.ReactNode,
    label: string,
    value: string,
    iconColor: string = "text-blue-600"
  ) => (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0`}>
          <div className={iconColor}>{icon}</div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
          <p className="font-semibold text-gray-900 text-sm leading-tight">
            {value === "Not provided" || value === "Not specified" ? (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                {value}
              </span>
            ) : (
              value
            )}
          </p>
        </div>
      </div>
    </div>
  );

  const renderSectionHeader = (
    icon: React.ReactNode,
    title: string,
    _bgColor: string,
    iconBgColor: string,
    iconColor: string
  ) => (
    <div className={`flex items-center gap-3 mb-6`}>
      <div className={`w-10 h-10 ${iconBgColor} rounded-xl flex items-center justify-center`}>
        <div className={iconColor}>{icon}</div>
      </div>
      <h2 className="text-xl font-bold text-gray-900">{title}</h2>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1000px] h-[95vh] flex flex-col p-0 rounded-xl overflow-hidden">
        <DialogHeader className="px-4 pb-0">
          <DialogTitle className="sr-only">
            Employee Details - {employee.user?.firstName || employee.firstName || "Employee"}{" "}
            {employee.user?.lastName || employee.lastName || ""}
          </DialogTitle>
          <DialogDescription className="sr-only">
            View complete employee information including contact details, professional information, job details, and employment information.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* Employee Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 shadow-sm">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {(employee.user?.firstName || employee.firstName || "N")[0]}
                  {(employee.user?.lastName || employee.lastName || "A")[0]}
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-1">
                    {employee.user?.firstName || employee.firstName || "Not provided"}{" "}
                    {employee.user?.lastName || employee.lastName || ""}
                  </h1>
                  <p className="text-gray-600 text-base font-medium mb-2">
                    Employee ID: {employee.user?.employeeId || employee.employeeId || "Not provided"}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-2">

                      {(() => {
                        const status = employee.jobInfo?.employmentStatus || (employee.user?.isActive ? "Active" : "Inactive");
                        return (
                          <Badge
                            variant="outline"
                            className={`text-xs ${status === "Active" || status === "Full-time"
                                ? "bg-green-50 text-green-700"
                                : status === "Inactive" || status === "Terminated"
                                  ? "bg-red-50 text-red-700"
                                  : status === "On Leave" || status === "Probation"
                                    ? "bg-yellow-50 text-yellow-700"
                                    : status === "Part-time" || status === "Contract"
                                      ? "bg-blue-50 text-blue-700"
                                      : status === "Intern"
                                        ? "bg-purple-50 text-purple-700"
                                        : "bg-gray-50 text-gray-700"
                              }`}
                          >
                            {status}
                          </Badge>
                        );
                      })()}
                    </span>
                    <span className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Created: {formatDate(employee.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div className="bg-blue-50 rounded-xl p-6 shadow-sm border border-blue-100">
                {renderSectionHeader(
                  <Mail className="h-5 w-5" />,
                  "Contact Information",
                  "bg-blue-50",
                  "bg-blue-100",
                  "text-blue-600"
                )}

                <div className="grid grid-cols-1 gap-4">
                  {renderInfoItem(
                    <Mail className="h-4 w-4" />,
                    "Email Address",
                    employee.user?.email || employee.email || "Not provided"
                  )}
                  {renderInfoItem(
                    <Phone className="h-4 w-4" />,
                    "Phone Number",
                    employee.user?.phone || employee.phone || "Not provided"
                  )}
                </div>
              </div>

              {/* Professional Details */}
              <div className="bg-green-50 rounded-xl p-6 shadow-sm border border-green-100">
                {renderSectionHeader(
                  <User className="h-5 w-5" />,
                  "Professional Details",
                  "bg-green-50",
                  "bg-green-100",
                  "text-green-600"
                )}

                <div className="grid grid-cols-1 gap-4">
                  {renderInfoItem(
                    <Building2 className="h-4 w-4" />,
                    "Department",
                    typeof employee.department === "object"
                      ? employee.department?.name
                      : employee.department || "Not specified",
                    "text-green-600"
                  )}
                  {renderInfoItem(
                    <Shield className="h-4 w-4" />,
                    "Roles",
                    (() => {
                      if (employee.user?.roles && Array.isArray(employee.user.roles)) {
                        if (employee.user.roles.length > 0) {
                          return employee.user.roles
                            .map((role: any) => (typeof role === "object" ? role.name || role : role))
                            .join(", ");
                        }
                      }
                      if (employee.role) {
                        return typeof employee.role === "object"
                          ? employee.role.name || "Not specified"
                          : employee.role;
                      }
                      return "Not specified";
                    })(),
                    "text-green-600"
                  )}
                  {renderInfoItem(
                    <IndianRupee className="h-4 w-4" />,
                    "Salary",
                    formatCurrency(employee.salary),
                    "text-green-600"
                  )}
                </div>
              </div>
            </div>

            {/* Job Information */}
            <div className="bg-blue-50 rounded-xl p-6 shadow-sm border border-blue-100">
              {renderSectionHeader(
                <Briefcase className="h-5 w-5" />,
                "Job Information",
                "bg-blue-50",
                "bg-blue-100",
                "text-blue-600"
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderInfoItem(
                  <Calendar className="h-4 w-4" />,
                  "Joining Date",
                  formatDate(employee.jobInfo?.joiningDate || employee.joiningDate || employee.joinDate)
                )}
                {renderInfoItem(
                  <Briefcase className="h-4 w-4" />,
                  "Job Title",
                  (() => {
                    const jobTitle = employee.jobInfo?.jobTitle;
                    if (!jobTitle) return "Not provided";
                    if (typeof jobTitle === "string") return jobTitle;
                    if (typeof jobTitle === "object" && jobTitle.title) return jobTitle.title;
                    return "Not provided";
                  })()
                )}
                {renderInfoItem(
                  <MapPin className="h-4 w-4" />,
                  "Location",
                  employee.jobInfo?.location || "Not provided"
                )}
                {renderInfoItem(
                  <Award className="h-4 w-4" />,
                  "Category",
                  employee.jobInfo?.category || "Not provided"
                )}
                {renderInfoItem(
                  <Users className="h-4 w-4" />,
                  "Reporting Manager",
                  managerNames.reportingManager
                )}
                {renderInfoItem(
                  <Users className="h-4 w-4" />,
                  "Line Manager",
                  managerNames.lineManager
                )}
                {renderInfoItem(
                  <Users className="h-4 w-4" />,
                  "Line Manager 2",
                  managerNames.lineManager2
                )}
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <div className="text-blue-600"><Activity className="h-4 w-4" /></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Employment Status</p>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const status = employee.jobInfo?.employmentStatus || "Active";
                          return (
                            <Badge
                              variant="outline"
                              className={`text-xs ${status === "Active" || status === "Full-time"
                                  ? "bg-green-50 text-green-700"
                                  : status === "Inactive" || status === "Terminated"
                                    ? "bg-red-50 text-red-700"
                                    : status === "On Leave" || status === "Probation"
                                      ? "bg-yellow-50 text-yellow-700"
                                      : status === "Part-time" || status === "Contract"
                                        ? "bg-blue-50 text-blue-700"
                                        : status === "Intern"
                                          ? "bg-purple-50 text-purple-700"
                                          : "bg-gray-50 text-gray-700"
                                }`}
                            >
                              {status}
                            </Badge>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                {employee.jobInfo?.employmentStatus === "Probation" && !!employee.jobInfo?.probationPeriod && (
                  renderInfoItem(
                    <Award className="h-4 w-4" />,
                    "Probation Period",
                    `${employee.jobInfo.probationPeriod} months`
                  )
                )}
                {employee.jobInfo?.employmentStatus === "Probation" && employee.jobInfo?.probationEndDate && (
                  renderInfoItem(
                    <Calendar className="h-4 w-4" />,
                    "Probation End Date",
                    formatDate(employee.jobInfo.probationEndDate)
                  )
                )}
                {!!employee.jobInfo?.feedbackRemainderDays && (
                  renderInfoItem(
                    <Clock className="h-4 w-4" />,
                    "Feedback Remainder Days",
                    `${employee.jobInfo.feedbackRemainderDays} days`
                  )
                )}

                {employee.jobInfo?.note && (
                  <div className="col-span-full">
                    {renderInfoItem(
                      <Info className="h-4 w-4" />,
                      "Notes",
                      employee.jobInfo.note
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Agreement Information */}
            {/* <div className="bg-green-50 rounded-xl p-6 shadow-sm border border-green-100">
              {renderSectionHeader(
                <FileText className="h-5 w-5" />,
                "Agreement Information",
                "bg-green-50",
                "bg-green-100",
                "text-green-600"
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderInfoItem(
                  <Calendar className="h-4 w-4" />,
                  "Start Date",
                  formatDate(employee.agreementInfo?.startDate),
                  "text-green-600"
                )}
                {renderInfoItem(
                  <Calendar className="h-4 w-4" />,
                  "End Date",
                  formatDate(employee.agreementInfo?.endDate),
                  "text-green-600"
                )}
                {renderInfoItem(
                  <Clock className="h-4 w-4" />,
                  "Period (Months)",
                  employee.agreementInfo?.periodInMonths || "Not provided",
                  "text-green-600"
                )}
                {renderInfoItem(
                  <Clock className="h-4 w-4" />,
                  "Notify Period (Days)",
                  employee.agreementInfo?.notifyPeriodInDays || "Not provided",
                  "text-green-600"
                )}

                {employee.agreementInfo?.note && (
                  <div className="col-span-full">
                    {renderInfoItem(
                      <Info className="h-4 w-4" />,
                      "Notes",
                      employee.agreementInfo.note,
                      "text-green-600"
                    )}
                  </div>
                )}
              </div>
            </div> */}

            {/* Compensation Information */}
            {/* <div className="bg-purple-50 rounded-xl p-6 shadow-sm border border-purple-100">
              {renderSectionHeader(
                <TrendingUp className="h-5 w-5" />,
                "Compensation Information",
                "bg-purple-50",
                "bg-purple-100",
                "text-purple-600"
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderInfoItem(
                  <Calendar className="h-4 w-4" />,
                  "Start Date",
                  formatDate(employee.compensationInfo?.startDate),
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <Calendar className="h-4 w-4" />,
                  "End Date",
                  formatDate(employee.compensationInfo?.endDate),
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <IndianRupee className="h-4 w-4" />,
                  "New Salary",
                  formatCurrency(employee.compensationInfo?.newSalary),
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <Clock className="h-4 w-4" />,
                  "Pay Frequency",
                  employee.compensationInfo?.payFrequency || "Not provided",
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <Clock className="h-4 w-4" />,
                  "Notice Period (Days)",
                  employee.compensationInfo?.noticePeriodInDays || "Not provided",
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <Clock className="h-4 w-4" />,
                  "Notify Period (Days)",
                  employee.compensationInfo?.notifyPeriodInDays || "Not provided",
                  "text-purple-600"
                )}
                {renderInfoItem(
                  <CreditCard className="h-4 w-4" />,
                  "Salary Mode",
                  employee.compensationInfo?.salaryMode || "Not provided",
                  "text-purple-600"
                )}

                {employee.compensationInfo?.note && (
                  <div className="col-span-full">
                    {renderInfoItem(
                      <Info className="h-4 w-4" />,
                      "Notes",
                      employee.compensationInfo.note,
                      "text-purple-600"
                    )}
                  </div>
                )}
              </div>
            </div> */}


          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 